import { useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import AccordionMenu from '../../../common/accordion-menu';
import Table from '../../../common/table';
import MultiSelect from '../../../common/multi-select';
import { countriesByRegions } from '../../../../config';
import l from '../../../../lang';
import { getCountryFlagUrl } from '../../../../utils';
import Loading from '../../../common/loading';
import { usePayoutProcessingFees, PayoutProcessingFee } from '../../../../hooks/use-payout-processing-fees';
import './styles.scss';

const formatRate = (row?: PayoutProcessingFee) => {
  if (!row) {
    return '-';
  }
  const percentage = Number(row.ratePercentage).toFixed(2);
  const fixed = Number(row.rateFixed).toFixed(2);
  return `${percentage}% + USD ${fixed}`;
};

interface PayoutFeesTableProps {
  countriesInRegion: string[];
  feesByCountry: Record<string, PayoutProcessingFee>;
}

function PayoutFeesTable({ countriesInRegion, feesByCountry }: PayoutFeesTableProps) {
  const [filteredCountries, setFilteredCountries] = useState('');
  const countriesWithFees = useMemo(
    () => countriesInRegion.filter(code => feesByCountry[code]),
    [countriesInRegion, feesByCountry],
  );

  const countryOptions = useMemo(
    () =>
      countriesWithFees.map(code => ({
        id: code,
        name: l(`countryCode.${code}`),
      })),
    [countriesWithFees],
  );

  const visibleCountries = useMemo(() => {
    if (!filteredCountries) {
      return countriesWithFees;
    }
    const selected = filteredCountries.split(',').filter(Boolean);
    return countriesWithFees.filter(code => selected.includes(code));
  }, [filteredCountries, countriesWithFees]);

  return (
    <div className="payout-fees__table-panel">
      <div className="payout-fees__filter">
        <MultiSelect
          name="filter-country"
          size="sm"
          label={l('countries.label')}
          placeholderSearch={l('searchCountries.label')}
          titleOptions={l('transactions.filter.country.titleOptions')}
          withSearch
          options={countryOptions}
          onChange={countries => setFilteredCountries(countries || '')}
        />
      </div>
      <div className="payout-fees__table">
        <Table
          dense
          cellAlign="left"
          withoutEdgeSpacers
          emptyBody={<></>}
          headers={[
            <div className="payout-fees__country-header">
              <Globe className="payout-fees__country-header-icon" size={16} strokeWidth={1.5} aria-hidden />
              <span>{l('paymentMethod.country')}</span>
            </div>,
            l('paymentMethod.payouts.header.rate'),
          ]}
          body={visibleCountries.map(code => {
            const row = feesByCountry[code];
            return [
              <div className="payout-fees__country-cell">
                <img alt="" className="payout-fees__country-flag" src={getCountryFlagUrl(code)} />
                <span>{l(`countryCode.${code}`)}</span>
              </div>,
              <span className="payout-fees__rate-cell">{formatRate(row)}</span>,
            ];
          })}
        />
      </div>

      <div className="payout-fees__footer">
        <span>{l('paymentMethod.payouts.footerNote')}</span>
      </div>
    </div>
  );
}

function PayoutFees() {
  const { data, loading, error } = usePayoutProcessingFees();

  const feesByCountry = useMemo(() => {
    const map: Record<string, PayoutProcessingFee> = {};
    data.forEach(item => {
      map[item.country] = item;
    });
    return map;
  }, [data]);

  const items = [
    {
      id: 'LA',
      title: l('region.LA'),
      countries: countriesByRegions.LA,
    },
    {
      id: 'AF_ME',
      title: l('region.AF_ME'),
      countries: countriesByRegions.AF_ME,
    },
    {
      id: 'AS',
      title: l('region.AS'),
      countries: countriesByRegions.AS,
    },
  ].map(region => {
    const hasCountries = region.countries.some(code => feesByCountry[code]);
    return {
      id: region.id,
      title: region.title,
      disabled: !hasCountries,
      content: hasCountries ? (
        <PayoutFeesTable countriesInRegion={region.countries} feesByCountry={feesByCountry} />
      ) : null,
    };
  });

  return (
    <div className="payout-fees">
      <div className="payment-methods__subtitle">{l('paymentMethod.payouts.subtitle')}</div>
      <div className="payment-methods__desc">{l('paymentMethod.payouts.desc')}</div>
      {loading && (
        <div className="payout-fees__loading">
          <Loading />
        </div>
      )}
      {error && !loading && <div className="payout-fees__error">{l('common.error')}</div>}
      {!loading && !error && (
        <div className="payout-fees__content">
          <AccordionMenu items={items} />
        </div>
      )}
    </div>
  );
}

export default PayoutFees;
